import type { AppLocale } from "@/lib/i18n/constants";
import { buildPageNotFoundTelemetry, type PageNotFoundTelemetryPayload } from "./telemetry";

type ConsentListener = (event: Event) => void;

export type PageNotFoundCaptureRuntime = {
  readonly consentEventName: string;
  readonly consentTarget: {
    addEventListener(type: string, listener: ConsentListener): void;
    removeEventListener(type: string, listener: ConsentListener): void;
  };
  readonly getPostHogTracker: (() => ((payload: PageNotFoundTelemetryPayload) => unknown) | undefined);
  readonly hasAnalyticsConsent: () => boolean;
  readonly locale: AppLocale;
  readonly origin: string;
  readonly pathname: string;
  readonly referrer: string;
  readonly userAgent: string;
};

export type PageNotFoundPayloadCaptureRuntime = Omit<
  PageNotFoundCaptureRuntime,
  "locale" | "origin" | "pathname" | "referrer" | "userAgent"
> & {
  readonly payload: PageNotFoundTelemetryPayload;
  readonly runtimeKey: string;
};

type CaptureAttempt = "accepted" | "already_accepted" | "sampled_out" | "waiting_for_consent" | "waiting_for_tracker";

export type PageNotFoundCaptureCoordinator = {
  readonly start: (runtime: PageNotFoundCaptureRuntime) => () => void;
};

export type PageNotFoundPayloadCaptureCoordinator = {
  readonly start: (runtime: PageNotFoundPayloadCaptureRuntime) => () => void;
};

type PreparedCaptureRuntime = Omit<
  PageNotFoundCaptureRuntime,
  "locale" | "origin" | "pathname" | "referrer" | "userAgent"
> & {
  readonly getPayload: () => PageNotFoundTelemetryPayload | null;
  readonly runtimeKey: string;
};

export function createPageNotFoundCaptureCoordinator(): PageNotFoundCaptureCoordinator {
  const preparedCoordinator = createPreparedCaptureCoordinator();

  return {
    start(runtime) {
      return preparedCoordinator.start({
        ...runtime,
        runtimeKey: `${runtime.origin}:${runtime.pathname}:${runtime.locale}`,
        getPayload: () =>
          buildPageNotFoundTelemetry(
            runtime.pathname,
            runtime.referrer,
            runtime.origin,
            runtime.userAgent,
            runtime.locale,
          ),
      });
    },
  };
}

export function createPageNotFoundPayloadCaptureCoordinator(): PageNotFoundPayloadCaptureCoordinator {
  const preparedCoordinator = createPreparedCaptureCoordinator();

  return {
    start(runtime) {
      return preparedCoordinator.start({
        ...runtime,
        getPayload: () => runtime.payload,
      });
    },
  };
}

function createPreparedCaptureCoordinator(): { readonly start: (runtime: PreparedCaptureRuntime) => () => void } {
  const acceptedRuntimeKeys = new Set<string>();

  return {
    start(runtime) {
      let listening = false;

      const stopListening = () => {
        if (!listening) return;
        runtime.consentTarget.removeEventListener(runtime.consentEventName, handleConsentChange);
        listening = false;
      };

      const attempt = (): CaptureAttempt => {
        if (acceptedRuntimeKeys.has(runtime.runtimeKey)) {
          return "already_accepted";
        }

        const payload = runtime.getPayload();

        if (!payload) {
          acceptedRuntimeKeys.add(runtime.runtimeKey);
          return "sampled_out";
        }

        if (!runtime.hasAnalyticsConsent()) {
          return "waiting_for_consent";
        }

        const tracker = runtime.getPostHogTracker();

        if (!tracker) {
          return "waiting_for_tracker";
        }

        acceptedRuntimeKeys.add(runtime.runtimeKey);

        try {
          tracker(payload);
          return "accepted";
        } catch {
          acceptedRuntimeKeys.delete(runtime.runtimeKey);
          return "waiting_for_tracker";
        }
      };

      const handleConsentChange: ConsentListener = () => {
        const outcome = attempt();

        if (isSettled(outcome)) {
          stopListening();
        }
      };

      const initialOutcome = attempt();

      if (!isSettled(initialOutcome)) {
        runtime.consentTarget.addEventListener(runtime.consentEventName, handleConsentChange);
        listening = true;
      }

      return stopListening;
    },
  };
}

export const pageNotFoundCaptureCoordinator = createPageNotFoundCaptureCoordinator();
export const pageNotFoundPayloadCaptureCoordinator = createPageNotFoundPayloadCaptureCoordinator();

function isSettled(outcome: CaptureAttempt): boolean {
  return outcome === "accepted" || outcome === "already_accepted" || outcome === "sampled_out";
}
